/**
 * Wiederverwendbare Zeile fuer Aufgaben-/Ticket-/Berichts-Listen mit
 * Kunden-Bezug.
 *
 * Cluster 1.1 (Probst-Besprechung 2026-06-25): Kunde primaer, Aufgabe
 * sekundaer. Das gilt fuer alle Listen in denen Aufgabe UND Kunde
 * gemeinsam auftauchen — d.h. NICHT innerhalb einer Kundenakte, wo der
 * Kunde bereits Kontext ist. Dort bleibt die Komponente ohne customerName
 * und degradiert automatisch zu „Titel oben, Meta unten".
 *
 * Verwendung:
 *   <TaskListItem
 *     customerName={t.customerName}
 *     title={t.title}
 *     status={<StatusBadge status={t.status} />}
 *     meta={[
 *       { icon: <MapPin size={11} />, text: t.location },
 *       { icon: <Calendar size={11} />, text: dateRange },
 *     ]}
 *     href={`/m/bauberichteV2/tasks/${t.id}`}
 *     variant="card"
 *   />
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface TaskListItemMeta {
  icon?: ReactNode;
  text: string;
  /** Optionale Tailwind-Klasse fuer den Text (z.B. Highlight-Farbe fuer Anzahl). */
  className?: string;
}

export interface TaskListItemProps {
  /** Kunde — Top-Line, fett. Wenn undefined/null: Titel wandert nach oben (Kundenakte-Kontext). */
  customerName?: string | null;
  /** Aufgabe / Ticket / Bericht — Sub-Line unter dem Kunden. */
  title: string;
  /** Beschreibungs-Text unter dem Titel (max 2 Zeilen). */
  description?: string | null;
  /** Optionaler Kopf-Marker (z.B. Ticket-Nummer + Priority). */
  eyebrow?: ReactNode;
  /** Meta-Chips unter Titel (Ort, Datum, Dauer, Kategorie). */
  meta?: Array<TaskListItemMeta | null | undefined | false>;
  /** Status-Badge (oben rechts). */
  status?: ReactNode;
  /** Weitere Info unter dem Status (z.B. Assignee, Anzahl Berichte). */
  trailing?: ReactNode;
  /** Icon links vor dem Content (z.B. Status-Icon in Kundenakte-Listen). */
  leadingIcon?: ReactNode;
  /** Icon rechts nach dem Content (z.B. Chevron/FileText). */
  trailingIcon?: ReactNode;
  /** Card = block-level mit Border/Rounded (Grid-tauglich). Row = kompakt fuer divide-y-Listen. */
  variant?: 'card' | 'row';
  /** Ausgewaehlt-Zustand (z.B. Disposition/Auswertung — linke Liste). */
  selected?: boolean;
  /** Zusaetzliche Optik-Modifier (opacity fuer Helfer, dimmed etc.). */
  className?: string;
  /** Link-Ziel — rendert einen React-Router-Link. */
  href?: string;
  /** Klick-Handler fuer Button-Verhalten (schliesst href aus). */
  onClick?: () => void;
}

/**
 * Kern-Rendering: Layout ist immer identisch — Kunde oben fett, Titel darunter,
 * Description, Meta-Chips. Variante steuert nur Padding + Border.
 */
function ItemContent({
  customerName, title, description, eyebrow, meta, status,
  trailing, leadingIcon, trailingIcon,
}: Pick<TaskListItemProps,
  'customerName' | 'title' | 'description' | 'eyebrow' | 'meta'
  | 'status' | 'trailing' | 'leadingIcon' | 'trailingIcon'
>) {
  // Wenn kein Kunde: Titel wird zur Top-Line (Kundenakte-Kontext, Kunde ist schon Header).
  const primary = customerName?.trim() ? customerName : title;
  const secondary = customerName?.trim() ? title : null;
  const cleanedMeta = (meta ?? []).filter(
    (m): m is TaskListItemMeta => Boolean(m) && typeof m === 'object' && Boolean((m as TaskListItemMeta).text),
  );

  return (
    <div className="flex items-start gap-3">
      {leadingIcon && <div className="flex-shrink-0 mt-0.5">{leadingIcon}</div>}
      <div className="flex-1 min-w-0">
        {eyebrow && <div className="mb-0.5 flex items-center gap-2">{eyebrow}</div>}
        <div className="font-medium text-slate-100 truncate">{primary}</div>
        {secondary && (
          <div className="text-sm text-slate-400 truncate mt-0.5">{secondary}</div>
        )}
        {description && (
          <div className="text-xs text-slate-500 mt-1 line-clamp-2">{description}</div>
        )}
        {cleanedMeta.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
            {cleanedMeta.map((m, i) => (
              <span key={i} className={`flex items-center gap-1 ${m.className ?? ''}`}>
                {m.icon}
                <span>{m.text}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      {(status || trailing) && (
        <div className="flex flex-col gap-1 items-end flex-shrink-0">
          {status}
          {trailing}
        </div>
      )}
      {trailingIcon && <div className="flex-shrink-0 mt-0.5">{trailingIcon}</div>}
    </div>
  );
}

/**
 * Wiederverwendbare Aufgaben-/Ticket-/Berichts-Zeile. Rendert Kunde OBEN, Titel
 * darunter (Cluster 1.1 Kunde-First). Wenn kein customerName gesetzt ist,
 * degradiert die Komponente automatisch — Titel wird Top-Line, keine
 * Kunden-Zeile.
 */
export default function TaskListItem({
  variant = 'card',
  selected = false,
  className = '',
  href,
  onClick,
  ...content
}: TaskListItemProps) {
  const cardClasses = variant === 'card'
    ? 'block p-4 rounded-lg border transition-colors ' + (
      selected
        ? 'bg-brand-600/15 border-brand-600/40'
        : 'bg-slate-900/50 border-slate-800 hover:border-slate-700 hover:bg-slate-800/40'
    )
    : 'block px-3 py-2.5 border-b border-slate-800/60 last:border-b-0 transition-colors ' + (
      selected
        ? 'bg-brand-600/10'
        : 'hover:bg-slate-800/30'
    );
  const merged = `${cardClasses} ${className}`.trim();
  const inner = <ItemContent {...content} />;

  if (href) {
    // 2026-07-13 (Joerg): from-State mitgeben, damit useBack() in der Detail-
    // Seite auf die genaue Liste (inkl. searchParams) zurueckspringt. Ohne den
    // Trick landet man z.B. aus einer Liste-View des Kalenders per Zurueck in
    // der Mobile-Default-Tagesansicht (Alex/Kiara v5-Fund).
    const from = typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : undefined;
    return <Link to={href} state={from ? { from } : undefined} className={merged}>{inner}</Link>;
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={`${merged} w-full text-left`}>
        {inner}
      </button>
    );
  }
  return <div className={merged}>{inner}</div>;
}
